import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { Menu as MenuIcon, User, LogOut, RefreshCcw, PanelLeft, PanelTop, Moon, Sun, Bell } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useSetup } from '../../../core/contexts/SetupContext';
import { useGlobalTheme } from '../../hooks/useGlobalTheme';
import { useLayout } from '../../../core/contexts/LayoutContext';
import { secureGetItem } from '../../utils/secureStorage';
import SyncStatusIndicator from '../SyncStatusIndicator';
import ModuleBadge from '../ModuleBadge';
import AIAgentTriggerButton from '../../../ai-agent/components/AIAgentTriggerButton';
import { getModuleAlertesPath } from '../../utils/moduleAlertesRoute';
import './ThemeToggle.css';
import { FACTURATION_SECTION_ARCHIVE_ROUTES } from '../../../pages/facturation/config/facturationSectionArchivesConfig';

const Header = ({ sidebarOpen, toggleSidebar, sidebarMobileOpen, toggleMobileSidebar, isLightTheme = false }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { logout } = useSetup();
  const { isDashboardLightMode, toggleDashboardTheme } = useGlobalTheme();
  const { layoutMode, toggleLayout, isSidebarMode, isTopBarMode } = useLayout();
  const [user, setUser] = useState({ username: '', role: '' });
  
  // Load user from localStorage (set by authService after login)
  useEffect(() => {
    try {
      const storedUser = secureGetItem('qawaem_user');
      console.log('📦 Header loading user from secure storage:', storedUser);
      if (storedUser) {
        console.log('✅ Header setting user:', storedUser);
        setUser({
          username: storedUser.username || t('common.user'),
          role: storedUser.role || t('sidebar.user_role')
        });
      } else {
        console.warn('⚠️ No user data in Header');
        setUser({
          username: t('common.user'),
          role: t('sidebar.user_role')
        });
      }
    } catch (error) {
      console.error('❌ Error loading user from secure storage:', error);
    }
  }, [t]);
  
  // Check if we're on pages that support theme toggle
  const isDashboardPage = location.pathname === '/' || location.pathname === '/dashboard';
  const isOverviewPage = location.pathname === '/overview';
  const isDocumentsPage =
    location.pathname === '/archives' ||
    FACTURATION_SECTION_ARCHIVE_ROUTES.includes(location.pathname);
  const isPaymentsPage = location.pathname === '/payments';
  const isFinancesPage = location.pathname === '/finances';
  const isClientsPage = location.pathname === '/clients';
  const isEmployeesPage = location.pathname === '/employees';
  const isDepartmentsPage = location.pathname === '/departments';
  // const isDeliveryTransportPage = location.pathname === '/delivery-transport'; // DELETED
  const isProductsPage = location.pathname === '/products';
  const isCategoriesPage = location.pathname === '/categories';
  const isReportsPage = location.pathname.startsWith('/reports');
  const isStockPage = location.pathname === '/stock';
  const isReturnsPage = location.pathname === '/returns';
  const isAnalysisPage = location.pathname.startsWith('/analysis');
  const isSettingsPage = location.pathname === '/settings';
  const isDocumentCreationPage =
    location.pathname === '/document-creation' ||
    location.pathname.startsWith('/document-creation/') ||
    location.pathname === '/inventaire/document-creation' ||
    location.pathname === '/creer-un-document';

  const isFacturationModulePage = location.pathname.startsWith('/facturation');
  
  // Combine all pages that should show the theme toggle
  const isThemePage = isDashboardPage || isOverviewPage || isDocumentsPage || isPaymentsPage || isFinancesPage || 
                     isClientsPage || isEmployeesPage || isDepartmentsPage || // isDeliveryTransportPage (DELETED) ||
                     isProductsPage || isCategoriesPage || isReportsPage || isStockPage || isReturnsPage || 
                     isAnalysisPage || isSettingsPage || isDocumentCreationPage || isFacturationModulePage;
  
  const navigate = useNavigate();

  const goToModuleAlertes = () => {
    navigate(getModuleAlertesPath(location.pathname));
  };

  const handleLogout = () => {
    if (logout) {
      logout();
    }
    // Redirect directly to login page (HashRouter)
    navigate('/login', { replace: true });
  };
  
  // State for user menu visibility
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Fermer le menu profil quand un module ouvre un overlay plein écran (ex. aperçu modèle facturation)
  useEffect(() => {
    const onCloseHeaderMenus = () => {
      setUserMenuOpen(false);
    };
    window.addEventListener('Qawaem-close-header-menus', onCloseHeaderMenus);
    return () => window.removeEventListener('Qawaem-close-header-menus', onCloseHeaderMenus);
  }, []);

  // Global refresh function
  const refreshAllData = () => {
    console.log('Refreshing all application data...');
    
    // Emit refresh events for all sections
    window.dispatchEvent(new CustomEvent('refresh-dashboard'));
    window.dispatchEvent(new CustomEvent('refresh-clients'));
    window.dispatchEvent(new CustomEvent('refresh-products'));
    window.dispatchEvent(new CustomEvent('refresh-categories'));
    window.dispatchEvent(new CustomEvent('refresh-employees'));
    window.dispatchEvent(new CustomEvent('refresh-documents'));
    window.dispatchEvent(new CustomEvent('refresh-settings'));
    
    // Also emit a general refresh event
    window.dispatchEvent(new CustomEvent('refresh-all-data'));
    
    // Show feedback to user
    console.log('All data refresh triggered');
  };

  return (
    <header 
      style={{ minHeight: '64px' }}
      className={`${isLightTheme 
        ? 'bg-white/90 border-b border-gray-200/50' 
        : 'bg-white/10 backdrop-blur-lg border-b border-white/20'
      } flex items-center justify-between px-4 md:px-6 z-20`}>
      {/* Left side - Menu button and page title */}
      <div className="flex items-center">
        <button
          className={`lg:hidden mr-4 ${isLightTheme 
            ? 'text-gray-700 hover:text-gray-900' 
            : 'text-white/80 hover:text-white'
          }`}
          onClick={toggleMobileSidebar}
        >
          <MenuIcon className="h-6 w-6" />
        </button>
        
        {/* Global refresh button */}
        <motion.button
          whileHover={{ scale: 1.1, rotate: 90 }}
          whileTap={{ scale: 0.9, rotate: 180 }}
          onClick={refreshAllData}
          className={`p-2 rounded-lg transition-all duration-200 ${isLightTheme 
            ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' 
            : 'text-white/60 hover:text-white hover:bg-white/10'
          }`}
          title={t('common.refresh')}
        >
          <RefreshCcw className="h-5 w-5" />
        </motion.button>
        
        {/* Module Badge - Shows current module */}
        <div className="ml-3">
          <ModuleBadge />
        </div>
      </div>

      {/* Right side - Sync Status, Notification, Theme toggle, Language switcher, Layout toggle and user menu */}
      <div className="flex items-center space-x-2">
        {/* Sync Status Indicator */}
        <SyncStatusIndicator />

        {/* AI Agent */}
        <AIAgentTriggerButton isLightTheme={isLightTheme} />
        
        {/* Notifications — cloche unique (pas de doublon dans les pages dashboard) */}
        <button
          type="button"
          onClick={goToModuleAlertes}
          className={`p-2 rounded-lg transition-all duration-200 relative ${isLightTheme
            ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            : 'text-white/80 hover:text-white hover:bg-white/10'
          }`}
          title={t('common.notifications')}
          aria-label={t('common.notifications')}
        >
          <Bell className="h-5 w-5" />
        </button>
        
        {/* Theme Toggle - Simple Icon Button */}
        <button
          onClick={toggleDashboardTheme}
          className={`p-2 rounded-lg transition-all duration-200 ${isLightTheme 
            ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' 
            : 'text-white/80 hover:text-white hover:bg-white/10'
          }`}
          title={isDashboardLightMode ? t('common.dark_mode') : t('common.light_mode')}
        >
          {isDashboardLightMode ? (
            <Moon className="h-5 w-5" />
          ) : (
            <Sun className="h-5 w-5" />
          )}
        </button>
        
        {/* Layout Toggle Button (Sidebar/TopBar) — id pour masquage cohérent avec aperçu modèle */}
        <div className="relative" id="Qawaem-header-layout-toggle-wrap">
          <button
            onClick={toggleLayout}
            className={`flex items-center space-x-1 p-2 rounded-lg transition-all duration-200 ${isLightTheme 
              ? 'text-gray-700 hover:bg-gray-100' 
              : 'text-white hover:bg-white/10'
            }`}
            title={isSidebarMode ? t('common.layout_switch_topbar') : t('common.layout_switch_sidebar')}
          >
            {isSidebarMode ? (
              <PanelTop className={`h-5 w-5 ${isLightTheme ? 'text-gray-600' : 'text-white/80'}`} />
            ) : (
              <PanelLeft className={`h-5 w-5 ${isLightTheme ? 'text-gray-600' : 'text-white/80'}`} />
            )}
          </button>
        </div>
        
        {/* User menu */}
        <div className="relative">
          <button
            className={`flex items-center space-x-2 p-2 rounded-full transition-colors ${isLightTheme 
              ? 'text-gray-900 hover:bg-gray-100' 
              : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
              {user?.username?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          </button>

          {userMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40"
                onClick={() => setUserMenuOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.1 }}
                className={`absolute right-0 mt-2 w-48 border rounded-lg shadow-lg z-50 overflow-hidden ${isLightTheme 
                  ? 'bg-white border-gray-200' 
                  : 'bg-white/10 backdrop-blur-lg border-white/20'
                }`}
              >
                <div className={`px-4 py-3 border-b ${isLightTheme ? 'border-gray-200' : 'border-white/10'}`}>
                  <p className={`text-sm ${isLightTheme ? 'text-gray-900' : 'text-white'}`}>{user?.username || t('common.user')}</p>
                  <p className={`text-xs truncate ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`}>{user?.role || t('sidebar.user_role')}</p>
                </div>
                <div className="py-1">
                  <Link
                    to="/profile"
                    className={`group flex w-full items-center px-4 py-2 text-sm transition-colors ${isLightTheme 
                      ? 'text-gray-900 hover:bg-gray-100' 
                      : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <User className={`h-4 w-4 mr-3 ${isLightTheme ? 'text-gray-700' : 'text-white/70'}`} />
                    {t('common.profile')}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className={`group flex w-full items-center px-4 py-2 text-sm transition-colors ${isLightTheme 
                      ? 'text-gray-900 hover:bg-gray-100' 
                      : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <LogOut className={`h-4 w-4 mr-3 ${isLightTheme ? 'text-gray-700' : 'text-white/70'}`} />
                    {t('common.logout')}
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;

